import { TemplateItem } from '@anm/api/modules/templates';
import Loader from '@anm/components/Loader';
import { memo, FC, PropsWithChildren } from 'react';
import Slider, { Settings } from 'react-slick';

import { CarouselWrapper, LoaderWrapper, SectionTitle, SliderHeader, SlideWrapper } from './Wrapper';
import { useCalcItemWidth, useSliderArrows } from './hooks';

export { SectionTitle };

const settings: Settings = {
  dots: true,
  swipe: false,
  infinite: false,
  variableWidth: true,
  adaptiveHeight: true,
  slidesToShow: 5,
  lazyLoad: 'ondemand',
  responsive: [
    {
      breakpoint: 4110,
      settings: {
        slidesToShow: 14,
        slidesToScroll: 13
      }
    },
    {
      breakpoint: 3800,
      settings: {
        slidesToShow: 13,
        slidesToScroll: 12
      }
    },
    {
      breakpoint: 3500,
      settings: {
        slidesToShow: 12,
        slidesToScroll: 11
      }
    },
    {
      breakpoint: 3196,
      settings: {
        slidesToShow: 11,
        slidesToScroll: 10
      }
    },
    {
      breakpoint: 2887,
      settings: {
        slidesToShow: 10,
        slidesToScroll: 9
      }
    },
    {
      breakpoint: 2587,
      settings: {
        slidesToShow: 9,
        slidesToScroll: 8
      }
    },
    {
      breakpoint: 2273,
      settings: {
        slidesToShow: 8,
        slidesToScroll: 7
      }
    },
    {
      breakpoint: 1964,
      settings: {
        slidesToShow: 6,
        slidesToScroll: 5
      }
    },
    {
      breakpoint: 1812,
      settings: {
        slidesToShow: 5,
        slidesToScroll: 4
      }
    },
    {
      breakpoint: 1531,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 3
      }
    },
    {
      breakpoint: 1250,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 2
      }
    },
    {
      breakpoint: 1170,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 3
      }
    },
    {
      breakpoint: 960,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 2
      }
    },
    {
      breakpoint: 695,
      settings: {
        swipe: true,
        slidesToShow: 2,
        slidesToScroll: 1
      }
    }
  ]
};

type TemplatesSliderProps = {
  title: string;
  isPending: boolean;
  list: TemplateItem[];
  renderItem(item: TemplateItem): JSX.Element;
};

const TemplatesSlider: FC<PropsWithChildren<TemplatesSliderProps>> = ({
  list,
  title,
  children,
  isPending,
  renderItem
}) => {
  const arrowSettings = useSliderArrows(list);
  const width = useCalcItemWidth();

  const showNoTemplateMessage = !isPending && !list.length;

  return (
    <CarouselWrapper {...{ width }}>
      <SliderHeader>
        <SectionTitle {...{ title }} level={2} />
        {children}
      </SliderHeader>
      {showNoTemplateMessage && <p>No templates found</p>}
      {isPending ? (
        <LoaderWrapper>
          <Loader size="large" color="blue" />
        </LoaderWrapper>
      ) : (
        <Slider {...{ ...settings, ...arrowSettings }}>
          {list.map(slideProps => (
            <SlideWrapper key={slideProps.templateId}>{renderItem(slideProps)}</SlideWrapper>
          ))}
        </Slider>
      )}
    </CarouselWrapper>
  );
};

export default memo(TemplatesSlider);
